import { type SessionV2 } from "@decentriq/core";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomLazyJob,
} from "features/mediaDataRoom/hooks/v3";
import {
  type AudienceV3,
  type PublishedDatasetsHashes,
} from "features/mediaDataRoom/models";
import {
  AudienceSizeEstimationError,
  type AudienceSizesResult,
} from "../models";
import useAudienceSizesState from "../useAudienceSizesState";
import { transformEstimatedAudienceSize } from "../utils";
import { getRawAudienceReference } from "./utils";

interface AudienceSizesHookPayload {
  dataRoomId: string;
  audiences: AudienceV3[] | undefined;
  session: SessionV2 | undefined;
  publishedDatasetsHashes: PublishedDatasetsHashes;
}

type AudienceSizesHookResult = AudienceSizesResult;

export const useAudienceSizes = ({
  dataRoomId,
  audiences,
  session,
  publishedDatasetsHashes,
}: AudienceSizesHookPayload): AudienceSizesHookResult => {
  const queryClient = useQueryClient();
  const [enableAudienceSizeEstimation, setEnableAudienceSizeEstimation] =
    useState<string[]>([]);
  const input = useMemo(
    () =>
      MediaDataRoomJobInput.create(
        "estimateAudienceSize",
        dataRoomId,
        publishedDatasetsHashes
      ),
    [dataRoomId, publishedDatasetsHashes]
  );
  const [estimateAudienceSize] = useMediaDataRoomLazyJob({
    input,
    session,
    transform: transformEstimatedAudienceSize,
  });
  const audienceSizes = useQueries({
    queries: enableAudienceSizeEstimation.map((id) => ({
      enabled:
        publishedDatasetsHashes.hasRequiredData &&
        audiences?.some((a) => a.id === id),
      queryFn: async () => {
        try {
          const audience = audiences?.find((a) => a.id === id);
          if (!audience) {
            return { [id]: 0 };
          }
          const size = await estimateAudienceSize({
            requestCreator: () => ({
              audience: {
                kind: "REF",
                value: getRawAudienceReference(audience),
              },
            }),
            updateInput: (input) => input.withResourceId(id),
          });
          return { [id]: size };
        } catch (error) {
          throw new AudienceSizeEstimationError(
            `Failed to estimate audience size for audience ${id}`,
            id,
            error
          );
        }
      },
      queryKey: input
        .withFetchResultsSubKey()
        .withResourceId(id)
        .buildQueryKey(),
      retry: false,
    })),
  });
  const retrySizeEstimationForAudience = useCallback(
    (audienceId: string) =>
      queryClient.refetchQueries({
        queryKey: input
          .withFetchResultsSubKey()
          .withResourceId(audienceId)
          .buildQueryKey(),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.queryChangingKey, queryClient]
  );
  const state = useAudienceSizesState({
    audienceSizes,
    audiences,
    enableAudienceSizeEstimation,
    setEnableAudienceSizeEstimation,
  });
  return useMemo(
    () => ({
      ...state,
      retrySizeEstimationForAudience,
    }),
    [state, retrySizeEstimationForAudience]
  );
};
